import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface HeroProps {
  onGetStarted: () => void;
}

export default function Hero({ onGetStarted }: HeroProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => setLoaded(true), 100);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="relative font-body">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="w-full px-6 sm:px-8 lg:px-12 py-4 sm:py-5 flex items-center justify-center">
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            {[
              { label: 'About', target: 'about' },
              { label: 'Services', target: 'whatido' },
              { label: 'Portfolio', target: 'portfolio' },
              { label: 'Pricing', target: 'pricing' },
              { label: 'Contact', target: 'contact' },
            ].map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.target)}
                className={`text-[13px] font-medium tracking-wide cursor-pointer whitespace-nowrap transition-all hover:text-[#F5C518] ${isScrolled ? 'text-[#555]' : 'text-white/80 hover:text-white'}`}
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => navigate('/portal/login')}
              className={`text-[13px] font-semibold tracking-wide cursor-pointer whitespace-nowrap transition-all border-2 rounded-md px-5 py-1.5 ${isScrolled ? 'text-[#1a1a1a] border-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white' : 'text-white border-white hover:bg-white hover:text-[#1a1a1a]'}`}
            >
              Sign In
            </button>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-md cursor-pointer transition-colors ${isScrolled ? 'text-[#1a1a1a]' : 'text-white'}`}
            aria-label="Toggle menu"
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-4-line'} text-xl`}></i>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`fixed inset-0 z-40 bg-[#1a1a1a] md:hidden transition-all duration-500 ${isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="flex flex-col items-center justify-center h-full gap-8">
          {[
            { label: 'About', target: 'about' },
            { label: 'Services', target: 'whatido' },
            { label: 'Portfolio', target: 'portfolio' },
            { label: 'Pricing', target: 'pricing' },
            { label: 'Contact', target: 'contact' },
          ].map((item, i) => (
            <button
              key={item.label}
              onClick={() => scrollToSection(item.target)}
              className="text-4xl font-display text-white hover:text-[#F5C518] transition-colors cursor-pointer tracking-wider"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {item.label.toUpperCase()}
            </button>
          ))}
          <button
            onClick={() => { setIsMenuOpen(false); navigate('/portal/login'); }}
            className="bg-white text-[#1a1a1a] px-8 py-3.5 rounded-md text-sm font-semibold hover:bg-[#F5C518] transition-all cursor-pointer whitespace-nowrap mt-2"
          >
            Sign In
          </button>
        </div>
      </div>

      {/* Hero Section — Split Screen */}
      <section className="relative min-h-screen w-full flex flex-col lg:flex-row overflow-hidden">
        {/* Left Side — Dark */}
        <div className="relative w-full lg:w-[55%] bg-[#1a1a1a] flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-24 pt-28 sm:pt-32 pb-16 lg:pb-0 lg:pt-0 min-h-[70vh] lg:min-h-screen">
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }}></div>

          {/* Orange accent line */}
          <div className={`absolute top-0 left-0 w-1 bg-[#F5C518] transition-all duration-[1.5s] ease-out ${loaded ? 'h-full' : 'h-0'}`}></div>

          {/* Floating accent shapes */}
          <div className={`absolute top-[15%] right-[10%] w-16 h-16 border border-[#F5C518]/20 rounded-full transition-all duration-1000 delay-700 ${loaded ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`}></div>
          <div className={`absolute bottom-[20%] right-[20%] w-3 h-3 bg-[#F5C518] rounded-full transition-all duration-1000 delay-1000 ${loaded ? 'opacity-100' : 'opacity-0'}`}></div>
          <div className={`absolute top-[40%] right-[5%] w-8 h-[1px] bg-[#F5C518]/40 transition-all duration-1000 delay-800 ${loaded ? 'opacity-100' : 'opacity-0'}`}></div>

          <div className="relative z-10 max-w-2xl">
            {/* Headline */}
            <div className="font-display mb-8">
              <h1 className={`text-white text-[56px] sm:text-[72px] md:text-[88px] lg:text-[96px] xl:text-[110px] leading-[0.9] tracking-[0.02em] transition-all duration-700 delay-150 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                YOUR BUSINESS
              </h1>
              <h1 className={`text-[56px] sm:text-[72px] md:text-[88px] lg:text-[96px] xl:text-[110px] leading-[0.9] tracking-[0.02em] transition-all duration-700 delay-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <span className="text-white">IS </span>
                <span className="text-[#F5C518]">REAL.</span>
              </h1>
              <div className={`flex items-center gap-4 mt-3 transition-all duration-700 delay-450 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <div className="h-[2px] w-12 sm:w-16 bg-[#F5C518]/60"></div>
                <h1 className="text-white/30 text-[40px] sm:text-[52px] md:text-[64px] lg:text-[72px] xl:text-[80px] leading-[0.9] tracking-[0.02em]">
                  YOUR WEBSITE
                </h1>
              </div>
              <h1 className={`text-[40px] sm:text-[52px] md:text-[64px] lg:text-[72px] xl:text-[80px] leading-[0.9] tracking-[0.02em] text-white/30 transition-all duration-700 delay-[550ms] ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                SHOULD BE <span className="text-[#F5C518]">TOO.</span>
              </h1>
            </div>

            {/* Subheadline */}
            <p className={`text-white/50 text-sm sm:text-base leading-relaxed max-w-md mb-10 transition-all duration-700 delay-[650ms] ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Strategic websites built and managed for small business owners who are ready to level up.
            </p>

            {/* CTA Buttons */}
            <div className={`flex flex-col sm:flex-row items-start gap-4 mb-14 transition-all duration-700 delay-[750ms] ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <button
                onClick={onGetStarted}
                className="group bg-[#F5C518] text-[#1a1a1a] px-8 py-3.5 rounded-md text-sm font-semibold hover:bg-[#D4A90E] transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-[#F5C518]/20 hover:shadow-xl hover:shadow-[#F5C518]/30 hover:-translate-y-0.5"
              >
                Get Started
              </button>
              <button
                onClick={() => scrollToSection('portfolio')}
                className="group text-white/60 hover:text-white px-6 py-3.5 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap border border-white/15 hover:border-white/40"
              >
                View My Work
              </button>
            </div>
          </div>
        </div>

        {/* Right Side — Image */}
        <div className="relative w-full lg:w-[45%] min-h-[50vh] lg:min-h-screen">
          <img
            src="https://static.readdy.ai/image/fadb589bdce069ad17e86fea08d34f73/9b9e1931fff7217bb4981d7128e31e1f.png"
            alt="Yasmine - Web Designer and Digital Strategist"
            className="w-full h-full object-cover object-top"
          />
          {/* Gradient overlay on image */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/40 via-transparent to-transparent lg:bg-gradient-to-r lg:from-[#1a1a1a]/30 lg:via-transparent lg:to-transparent"></div>

          {/* Floating card on image */}
          <div className={`absolute bottom-8 right-6 sm:bottom-12 sm:right-10 bg-white/95 backdrop-blur-sm rounded-lg p-4 sm:p-5 shadow-xl max-w-[220px] transition-all duration-700 delay-[1100ms] ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <span className="text-[11px] font-semibold text-[#1a1a1a] tracking-wide uppercase">Available Now</span>
            </div>
            <p className="text-[#555] text-xs leading-relaxed">Taking on new projects. Let&apos;s build something amazing together.</p>
          </div>

          {/* Orange accent corner */}
          <div className={`absolute top-0 right-0 w-20 h-20 transition-all duration-1000 delay-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
            <div className="absolute top-0 right-0 w-full h-[3px] bg-[#F5C518]"></div>
            <div className="absolute top-0 right-0 h-full w-[3px] bg-[#F5C518]"></div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          50% { transform: translateY(300%); }
          100% { transform: translateY(-100%); }
        }
        @keyframes modalFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalSlideUp {
          from { opacity: 0; transform: scale(0.95) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
